import { ErrorBoundary } from '@sentry/react';
import { Button, ButtonGroup } from '@statsbomb/kitbag-components';
import { useAtomValue } from 'jotai';
import { Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { trackRadarDataFormatEvent } from '@/analytics/snowplow/tracking';
import { settingsConfigAtom } from '@/atoms/queries/userConfigs';
import { radarDataFormatAtom } from '@/atoms/radar';
import { RADAR_DATA_FORMAT } from '@/consts/searchKeys';
import { useSetQueryParams } from '@/hooks/queryParams/useSetQueryParams';
import { useOptimisticUserConfigUpdate } from '@/hooks/useOptimisticUserConfigUpdate';
import { RadarDataFormat } from '@/types/radar';
import { UserConfigPutMutationParams } from '@/types/userConfigs';
import { noop } from '@/utils/noop';
import { GeneralErrorBoundaryFallback } from '../errorBoundary/ErrorBoundaryFallback';

const RadarDataFormatToggleBase = ({
  activeRadarDataFormat = RadarDataFormat.Per90,
  onClick = noop,
  disabled = false,
}: {
  activeRadarDataFormat?: RadarDataFormat;
  onClick?: (newFormat: RadarDataFormat) => void;
  disabled?: boolean;
}) => {
  const { t } = useTranslation('visualisation');

  const isPer90Active = activeRadarDataFormat === RadarDataFormat.Per90;
  const isPercentilesActive = activeRadarDataFormat === RadarDataFormat.Percentiles;

  return (
    <ButtonGroup>
      <Button
        onClick={() => onClick(RadarDataFormat.Per90)}
        shape="pill"
        size="small"
        variant={isPer90Active ? 'primary' : 'ghost'}
        ariaPressed={isPer90Active}
        disabled={disabled}
      >
        {t('radarDataFormat.per90')}
      </Button>
      <Button
        onClick={() => onClick(RadarDataFormat.Percentiles)}
        shape="pill"
        size="small"
        variant={isPercentilesActive ? 'primary' : 'ghost'}
        ariaPressed={isPercentilesActive}
        disabled={disabled}
      >
        {t('radarDataFormat.globalPercentiles')}
      </Button>
    </ButtonGroup>
  );
};

const RadarDataFormatToggleWithData = () => {
  const settingsConfig = useAtomValue(settingsConfigAtom);
  const setQueryParams = useSetQueryParams();
  const radarDataFormat = useAtomValue(radarDataFormatAtom);

  const { updateUserConfigOptimistically } = useOptimisticUserConfigUpdate(radarDataFormat);

  const handleClick = async (newFormat: RadarDataFormat) => {
    if (newFormat === radarDataFormat) return;

    setQueryParams(RADAR_DATA_FORMAT, newFormat);
    trackRadarDataFormatEvent({
      previousConfiguration: radarDataFormat,
      updatedConfiguration: newFormat,
    });

    const existingDefinition = settingsConfig?.definition;
    const mutationParams: UserConfigPutMutationParams = {
      configId: settingsConfig?.configId,
      configType: 'settings',
      configDefinition: {
        ...existingDefinition,
        radar: { ...existingDefinition?.radar, dataFormat: newFormat },
      },
    };
    await updateUserConfigOptimistically(mutationParams, newFormat);
  };

  return <RadarDataFormatToggleBase activeRadarDataFormat={radarDataFormat} onClick={handleClick} />;
};

export const RadarDataFormatToggle = () => (
  <ErrorBoundary fallback={<GeneralErrorBoundaryFallback />}>
    <Suspense fallback={<RadarDataFormatToggleBase disabled />}>
      <RadarDataFormatToggleWithData />
    </Suspense>
  </ErrorBoundary>
);
